// ─────────────────────────────────────────────────────────────────────────────
// Status / accent tones — SINGLE SOURCE OF TRUTH.
// Defined once here; both light and dark themes derive their CSS variables from it
// (see light.js / dark.js, which spread `toneVars("light" | "dark")`).
//
// For each hue:
//   fg   = readable foreground (text / icon / border base)
//   soft = subtle background fill
// Light: dark saturated fg + pale pastel fill.  Dark: bright fg + low dark-tinted fill.
// Values are space-separated RGB channels so Tailwind's `<alpha-value>` works
// (e.g. text-tone-blue, bg-tone-blue-soft, border-tone-blue/30).
// Edit a value here once to restyle that tone across the whole app.
//
// Every `fg` reaches at least 4.5:1 against its own `soft` fill, because the
// commonest use of a tone is small text on that fill — a status chip, a badge,
// a callout. That is the tightest pair each tone has to clear; the same fg on a
// page or card background is lighter work. Four of the light values were
// darkened one Tailwind step to get there (blue, rose, emerald and orange sat
// between 3.1:1 and 4.3:1). Measure the pair again before changing one.
//
//        light fg on light soft        dark fg on dark soft
// blue      5.5:1                         9.3:1
// rose      5.2:1                        10.0:1
// amber     4.5:1                        (bright on near-black)
// emerald   4.8:1                        (bright on near-black)
// violet    4.8:1                        (bright on near-black)
// orange    4.5:1                         5.5:1
// purple    4.6:1                        (bright on near-black)
// slate     6.9:1                         6.8:1
// ─────────────────────────────────────────────────────────────────────────────
export const TONES = {
  blue: { fg: { light: "29 78 216", dark: "147 197 253" }, soft: { light: "219 234 254", dark: "10 30 54" } },
  rose: { fg: { light: "190 18 60", dark: "252 165 165" }, soft: { light: "255 228 230", dark: "32 10 10" } },
  amber: { fg: { light: "180 83 9", dark: "253 224 71" }, soft: { light: "254 243 199", dark: "28 26 0" } },
  emerald: { fg: { light: "4 120 87", dark: "110 231 183" }, soft: { light: "209 250 229", dark: "1 31 20" } },
  violet: { fg: { light: "124 58 237", dark: "233 213 255" }, soft: { light: "237 233 254", dark: "30 10 53" } },
  orange: { fg: { light: "194 65 12", dark: "251 146 60" }, soft: { light: "255 237 213", dark: "64 50 38" } },
  purple: { fg: { light: "147 51 234", dark: "192 132 252" }, soft: { light: "243 232 255", dark: "56 48 64" } },
  slate: { fg: { light: "71 85 105", dark: "148 163 184" }, soft: { light: "241 245 249", dark: "22 27 34" } },
};

// Build the `--tone-*` CSS variable map for a given theme mode ("light" | "dark").
export const toneVars = (mode) =>
  Object.fromEntries(
    Object.entries(TONES).flatMap(([name, { fg, soft }]) => [
      [`--tone-${name}`, fg[mode]],
      [`--tone-${name}-soft`, soft[mode]],
    ]),
  );
